import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/toaster";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import { usePageTitle } from "./hooks/usePageTitle";

// Public Pages
import Home from "./pages/Home";
import JadwalHasil from "./pages/JadwalHasil";
import Klasemen from "./pages/Klasemen";
import TopSkor from "./pages/TopSkor";
import Statistik from "./pages/Statistik";
import Peserta from "./pages/Peserta";
import Berita from "./pages/Berita";
import Galeri from "./pages/Galeri";
import TentangKami from "./pages/TentangKami";
import Event from "./pages/Event";
import EventDetail from "./pages/EventDetail";
import Draw from "./pages/Draw";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminAdvertisements from "./pages/admin/AdminAdvertisements";
import AdminNews from "./pages/admin/AdminNews";
import AdminPhotos from "./pages/admin/AdminPhotos";
import AdminVideos from "./pages/admin/AdminVideos";
import AdminMatches from "./pages/admin/AdminMatches";
import AdminStandings from "./pages/admin/AdminStandings";
import AdminTopScorers from "./pages/admin/AdminTopScorers";
import AdminParticipants from "./pages/admin/AdminParticipants";
import AdminPlayers from "./pages/admin/AdminPlayers";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminClubAdmins from "./pages/admin/AdminClubAdmins";
import AdminAuditLogs from "./pages/admin/AdminAuditLogs";
import AdminSchoolReference from "./pages/admin/AdminSchoolReference";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminEventTeams from "./pages/admin/AdminEventTeams";
import AdminEventBrackets from "./pages/admin/AdminEventBrackets";
import AdminEventMatches from "./pages/admin/AdminEventMatches";
import AdminMasterData from "./pages/admin/AdminMasterData";
import AdminContactSettings from "./pages/admin/AdminContactSettings";

// Component to handle page title
function PageTitleHandler({ children }) {
  usePageTitle();
  return children;
}

function AppRoutes() {
  return (
    <PageTitleHandler>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <>
            <Header />
            <Home />
            <Footer />
          </>
        } />
        <Route path="/jadwal-hasil" element={
          <>
            <Header />
            <JadwalHasil />
            <Footer />
          </>
        } />
        <Route path="/klasemen" element={
          <>
            <Header />
            <Klasemen />
            <Footer />
          </>
        } />
        <Route path="/draw" element={
          <>
            <Header />
            <Draw />
            <Footer />
          </>
        } />
        <Route path="/top-skor" element={<Navigate to="/statistik" replace />} />
        <Route path="/statistik" element={
          <>
            <Header />
            <Statistik />
            <Footer />
          </>
        } />
        <Route path="/peserta" element={<Navigate to="/peserta-sekolah" replace />} />
        <Route path="/peserta-klub" element={
          <>
            <Header />
            <Peserta participantType="club" />
            <Footer />
          </>
        } />
        <Route path="/peserta-sekolah" element={
          <>
            <Header />
            <Peserta participantType="school" />
            <Footer />
          </>
        } />
        <Route path="/berita" element={
          <>
            <Header />
            <Berita />
            <Footer />
          </>
        } />
        <Route path="/berita/:id" element={
          <>
            <Header />
            <Berita />
            <Footer />
          </>
        } />
        <Route path="/galeri" element={
          <>
            <Header />
            <Galeri />
            <Footer />
          </>
        } />
        <Route path="/tentang-kami" element={
          <>
            <Header />
            <TentangKami />
            <Footer />
          </>
        } />
        <Route path="/event" element={
          <>
            <Header />
            <Event />
            <Footer />
          </>
        } />
        <Route path="/event/:id" element={
          <>
            <Header />
            <EventDetail />
            <Footer />
          </>
        } />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/banners" element={<ProtectedRoute><AdminBanners /></ProtectedRoute>} />
        <Route path="/admin/advertisements" element={<ProtectedRoute><AdminAdvertisements /></ProtectedRoute>} />
        <Route path="/admin/news" element={<ProtectedRoute><AdminNews /></ProtectedRoute>} />
        <Route path="/admin/photos" element={<ProtectedRoute><AdminPhotos /></ProtectedRoute>} />
        <Route path="/admin/videos" element={<ProtectedRoute><AdminVideos /></ProtectedRoute>} />
        <Route path="/admin/matches" element={<ProtectedRoute><AdminMatches /></ProtectedRoute>} />
        <Route path="/admin/standings" element={<ProtectedRoute><AdminStandings /></ProtectedRoute>} />
        <Route path="/admin/top-scorers" element={<ProtectedRoute><AdminTopScorers /></ProtectedRoute>} />
        <Route path="/admin/club-admins" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminClubAdmins /></ProtectedRoute>} />
        <Route path="/admin/audit-logs" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminAuditLogs /></ProtectedRoute>} />
        <Route path="/admin/participants" element={<ProtectedRoute><AdminParticipants participantType="club" /></ProtectedRoute>} />
        <Route path="/admin/school-participants" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminParticipants participantType="school" /></ProtectedRoute>} />
        <Route path="/admin/players" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminPlayers /></ProtectedRoute>} />
        <Route path="/admin/school-reference" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminSchoolReference /></ProtectedRoute>} />
        <Route path="/admin/events" element={<ProtectedRoute><AdminEvents /></ProtectedRoute>} />
        <Route path="/admin/events/teams" element={<ProtectedRoute><AdminEventTeams /></ProtectedRoute>} />
        <Route path="/admin/events/brackets" element={<ProtectedRoute><AdminEventBrackets /></ProtectedRoute>} />
        <Route path="/admin/events/matches" element={<ProtectedRoute><AdminEventMatches /></ProtectedRoute>} />
        <Route path="/admin/master-data" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminMasterData /></ProtectedRoute>} />
        <Route path="/admin/hubungi-kami" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminContactSettings /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      </Routes>
    </PageTitleHandler>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
  );
}

export default App;
